import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  ArrowUpRight,
  Briefcase,
  MapPin,
  Clock,
  FlaskConical,
  ShieldCheck,
  TrendingUp,
  Award,
  Loader2,
  Mail,
  Phone,
  CheckCircle2,
  ChevronRight,
  Fuel,
  IndianRupee,
  Zap,
  UploadCloud,
  FileText,
  X,
} from "lucide-react";
import { MaskLine, Reveal } from "../components/Reveal";
import Marquee from "../components/Marquee";
import SEO, { SEO_CONFIG } from "../components/SEO";
import { submitCareerApplication, fetchCareerFormSchema } from "../lib/api";
import { useLanguage } from "../i18n/LanguageContext";

const NAME_PATTERN = /^[A-Za-z]+(?:\s+[A-Za-z]+)*$/;
const PHONE_PATTERN = /^\d{10}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LineInput = ({ label, error, required, ...props }) => (
  <div>
    <label className="label mb-3 block text-muted-foreground">
      {label} {required && <span className="text-primary">*</span>}
    </label>
    <input
      {...props}
      className="w-full border-0 border-b border-black/20 bg-transparent px-0 py-3 font-display text-lg text-foreground outline-none transition-colors placeholder:text-black/25 focus:border-primary"
    />
    {error ? <p className="mt-2 text-sm text-primary">{error}</p> : null}
  </div>
);

const LineSelect = ({ label, error, required, children, ...props }) => (
  <div>
    <label className="label mb-3 block text-muted-foreground">
      {label} {required && <span className="text-primary">*</span>}
    </label>
    <div className="relative">
      <select
        {...props}
        className="w-full appearance-none border-0 border-b border-black/20 bg-transparent px-0 py-3 font-display text-lg text-foreground outline-none transition-colors focus:border-primary cursor-pointer"
      >
        {children}
      </select>
      <div className="pointer-events-none absolute right-0 top-1/2 -translate-y-1/2 text-muted-foreground">
        <ChevronRight className="h-4 w-4 rotate-90" />
      </div>
    </div>
    {error ? <p className="mt-2 text-sm text-primary">{error}</p> : null}
  </div>
);

const POSITIONS = [
  {
    id: "fse-jaipur",
    department: "Sales & Technical",
    title: "Field Sales Executive — Jaipur",
    roleName: "Field Sales Executive",
    location: "Jaipur (1 Vacancy)",
    locationCity: "Jaipur",
    vacancies: "1 Vacancy",
    salary: "₹20,000 – ₹30,000 / month",
    fuel: "Petrol expenses for official company work only (Home commute not covered)",
    type: "Full-time · On-field",
    experience: "Freshers / 0 – 3 Years",
    urgent: true,
    summary:
      "Drive field sales, dealer network expansion, contractor engagement, and project acquisitions across Jaipur for Ashlar tile adhesives and construction chemicals. Candidates with experience in building materials or construction chemicals sales preferred; freshers with strong communication and sales skills are encouraged to apply.",
    tags: ["Urgent Hiring", "Jaipur", "1 Vacancy", "Field Sales", "Building Materials", "Construction Chemicals"],
  },
  {
    id: "fse-sikar",
    department: "Sales & Technical",
    title: "Field Sales Executive — Sikar",
    roleName: "Field Sales Executive",
    location: "Sikar (1 Vacancy)",
    locationCity: "Sikar",
    vacancies: "1 Vacancy",
    salary: "₹20,000 – ₹30,000 / month",
    fuel: "Petrol expenses for official company work only (Home commute not covered)",
    type: "Full-time · On-field",
    experience: "Freshers / 0 – 3 Years",
    urgent: true,
    summary:
      "Drive direct field sales, retail distribution, dealer management, and contractor relationships across Sikar for Ashlar high-bond adhesives and specialty construction chemicals. Building materials/chemicals sales experience preferred; dynamic freshers with persuasive communication skills are welcome.",
    tags: ["Urgent Hiring", "Sikar", "1 Vacancy", "Field Sales", "Building Materials", "Construction Chemicals"],
  },
  {
    id: "fse-dausa",
    department: "Sales & Technical",
    title: "Field Sales Executive — Dausa",
    roleName: "Field Sales Executive",
    location: "Dausa (1 Vacancy)",
    locationCity: "Dausa",
    vacancies: "1 Vacancy",
    salary: "₹20,000 – ₹30,000 / month",
    fuel: "Petrol expenses for official company work only (Home commute not covered)",
    type: "Full-time · On-field",
    experience: "Freshers / 0 – 3 Years",
    urgent: true,
    summary:
      "Expand Ashlar's sales footprint across Dausa through contractor meets, dealer onboarding, site visits, and institutional sales. Prior experience in construction chemicals or building materials sales preferred; freshers with excellent negotiation and field sales drive can also apply.",
    tags: ["Urgent Hiring", "Dausa", "1 Vacancy", "Field Sales", "Building Materials", "Construction Chemicals"],
  },
];

export default function Careers() {
  const { t, lang } = useLanguage();
  const formRef = useRef(null);

  const [activeTab, setActiveTab] = useState("all");
  const [schemaOptions, setSchemaOptions] = useState({
    positions: ["Field Sales Executive", "Open / General Application"],
    experience: ["Fresher / Entry Level", "1 - 3 Years", "3 - 5 Years", "5+ Years"],
    noticePeriods: ["Immediate Joining", "15 Days", "30 Days", "60+ Days"],
  });

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    position: "Field Sales Executive",
    location: "Jaipur",
    experience: "Fresher / Entry Level",
    company: "",
    noticePeriod: "Immediate Joining",
    resumeUrl: "",
    message: "",
  });

  const [resumeFile, setResumeFile] = useState(null);
  const [resumeMode, setResumeMode] = useState("file"); // "file" | "url"
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    let mounted = true;
    fetchCareerFormSchema().then((schema) => {
      if (!mounted || !schema || !Array.isArray(schema.fields)) return;
      const posField = schema.fields.find((f) => f.name === "position-applied-for");
      const expField = schema.fields.find((f) => f.name === "total-experience");
      const noticeField = schema.fields.find((f) => f.name === "notice-period");

      const positions = posField?.options?.length ? posField.options : ["Field Sales Executive", "Open / General Application"];
      const experience = expField?.options?.length ? expField.options : ["Fresher / Entry Level", "1 - 3 Years", "3 - 5 Years", "5+ Years"];
      const noticePeriods = noticeField?.options?.length ? noticeField.options : ["Immediate Joining", "15 Days", "30 Days", "60+ Days"];

      setSchemaOptions({
        positions,
        experience,
        noticePeriods,
      });

      setForm((prev) => ({
        ...prev,
        position: prev.position || positions[0],
        experience: prev.experience || experience[0],
        noticePeriod: prev.noticePeriod || noticePeriods[0],
      }));
    });
    return () => {
      mounted = false;
    };
  }, []);

  const setField = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const onNameChange = (e) => {
    const val = e.target.value;
    if (val === "" || /^[A-Za-z\s]*$/.test(val)) {
      setForm((prev) => ({ ...prev, name: val }));
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  const onPhoneChange = (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 10);
    setForm((prev) => ({ ...prev, phone: val }));
    setErrors((prev) => ({ ...prev, phone: "" }));
  };

  const handleFileSelect = (file) => {
    if (!file) return;

    // Validate extension & MIME type: accepts .pdf & .docx only
    const isExtValid = /\.(pdf|docx)$/i.test(file.name);
    const validTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/msword",
    ];

    if (!isExtValid && !validTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, resume: t("careers.fileTypeError") }));
      toast.error(t("careers.fileTypeError"));
      return;
    }

    // Validate size: max 10MB
    if (file.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, resume: t("careers.fileSizeError") }));
      toast.error(t("careers.fileSizeError"));
      return;
    }

    // Crucial requirement: "don't upload file until user it submit"
    // Stored in component state only, no server upload here
    setResumeFile(file);
    setErrors((prev) => ({ ...prev, resume: "" }));
  };

  const removeSelectedFile = () => {
    setResumeFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const onDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const selectRoleAndScroll = (roleTitle, roleLocation) => {
    const matchedPosition =
      schemaOptions.positions.find((p) =>
        roleTitle.toLowerCase().includes(p.toLowerCase()) ||
        p.toLowerCase().includes("field sales")
      ) ||
      schemaOptions.positions[0] ||
      "Field Sales Executive";

    setForm((prev) => ({
      ...prev,
      position: matchedPosition,
      ...(roleLocation ? { location: roleLocation } : {}),
    }));
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const submitApplication = async (e) => {
    e.preventDefault();
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = t("careers.errRequired");
    } else if (!NAME_PATTERN.test(form.name.trim())) {
      newErrors.name = t("careers.errName");
    }

    if (!form.email.trim()) {
      newErrors.email = t("careers.errRequired");
    } else if (!EMAIL_PATTERN.test(form.email.trim())) {
      newErrors.email = t("careers.errEmail");
    }

    if (!form.phone.trim()) {
      newErrors.phone = t("careers.errRequired");
    } else if (!PHONE_PATTERN.test(form.phone.trim())) {
      newErrors.phone = t("careers.errPhone");
    }

    if (!form.position) {
      newErrors.position = t("careers.errRequired");
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error(t("careers.errRequired"));
      return;
    }

    setSubmitting(true);
    try {
      // Resume file is uploaded ONLY here upon submission ("don't upload file until user it submit")
      await submitCareerApplication(form, resumeFile);
      setSubmitted(true);
      toast.success(t("careers.successMsg"));
      setForm({
        name: "",
        email: "",
        phone: "",
        position: schemaOptions.positions[0] || "Field Sales Executive",
        location: "Jaipur",
        experience: schemaOptions.experience[0] || "Fresher / Entry Level",
        company: "",
        noticePeriod: schemaOptions.noticePeriods[0] || "Immediate Joining",
        resumeUrl: "",
        message: "",
      });
      setResumeFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setErrors({});
    } catch (err) {
      toast.error(err.message || "Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const filteredPositions = POSITIONS.filter((pos) => {
    if (activeTab === "all") return true;
    if (activeTab === "jaipur") return pos.locationCity === "Jaipur";
    if (activeTab === "sikar") return pos.locationCity === "Sikar";
    if (activeTab === "dausa") return pos.locationCity === "Dausa";
    return true;
  });

  const careersSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: SEO_CONFIG.careers.title,
    description: SEO_CONFIG.careers.description,
    url: SEO_CONFIG.careers.canonical,
    publisher: {
      "@type": "Organization",
      name: "Ashlar Buildchem",
      url: "https://www.ashlarbuildchem.com",
      logo: "https://www.ashlarbuildchem.com/logo.png",
    },
    mainEntity: {
      "@type": "ItemList",
      itemListElement: POSITIONS.map((pos, idx) => ({
        "@type": "JobPosting",
        position: idx + 1,
        title: pos.title,
        description: pos.summary,
        hiringOrganization: {
          "@type": "Organization",
          name: "Ashlar Buildchem",
          sameAs: "https://www.ashlarbuildchem.com",
        },
        jobLocation: {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            addressLocality: pos.locationCity || "Jaipur",
            addressRegion: "Rajasthan",
            addressCountry: "IN",
          },
        },
        employmentType: "FULL_TIME",
        baseSalary: {
          "@type": "MonetaryAmount",
          currency: "INR",
          value: {
            "@type": "QuantitativeValue",
            minValue: 20000,
            maxValue: 30000,
            unitText: "MONTH",
          },
        },
      })),
    },
  };

  return (
    <div className="pt-20" data-testid="careers-page">
      <SEO {...SEO_CONFIG.careers} schema={careersSchema} />

      {/* Hero Section */}
      <section className="mx-auto max-w-[1600px] px-6 pb-20 pt-20 md:px-10 md:pt-28">
        <span className="label text-primary">{t("careers.eyebrow")}</span>
        <h1
          className={`mt-6 max-w-5xl font-display text-4xl font-bold sm:text-6xl md:text-[min(7.5vw,6.5rem)] ${
            lang === "hi"
              ? "normal-case leading-[1.15] tracking-normal"
              : "uppercase leading-[0.88] tracking-tighter"
          }`}
        >
          <MaskLine delay={0.05}>{t("careers.h1a")}</MaskLine>
          <MaskLine delay={0.18}>{t("careers.h1b")}</MaskLine>
          <MaskLine delay={0.3}>
            <span className="text-primary">{t("careers.h1c")}</span>
          </MaskLine>
        </h1>
        <Reveal delay={0.4} className="mt-10 flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <p className="max-w-2xl text-lg leading-relaxed text-muted-foreground">
            {t("careers.intro")}
          </p>
          <div className="flex flex-wrap items-center gap-4">
            <button
              onClick={() => {
                const el = document.getElementById("open-positions");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="careers-hero-explore"
              className="group inline-flex items-center gap-3 bg-primary px-8 py-5 text-primary-foreground transition-colors duration-200 hover:bg-foreground hover:text-background"
            >
              <span className="label">{t("careers.exploreOpenings")}</span>
              <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />
            </button>
            <button
              onClick={() => {
                if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth" });
              }}
              data-testid="careers-hero-apply"
              className="inline-flex items-center gap-2 border border-black/15 px-8 py-5 text-foreground transition-colors duration-200 hover:border-primary hover:text-primary"
            >
              <span className="label">{t("careers.applyDirectly")}</span>
            </button>
          </div>
        </Reveal>
      </section>

      {/* Marquee Ticker */}
      <section className="border-y border-black/10 py-6">
        <Marquee
          items={[
            "ISO 9001:2015 CERTIFIED",
            "ISI MARKED CHEMISTRY",
            "POLYMER FORMULATIONS",
            "STATE-OF-THE-ART LAB",
            "AUTOMATED DRY-MIX PLANT",
            "PAN-INDIA INFRASTRUCTURE",
          ]}
          duration={36}
        />
      </section>

      {/* Stats Bar */}
      <section className="mx-auto max-w-[1600px] px-6 py-16 md:px-10">
        <div className="grid gap-px bg-black/10 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-background p-8 md:p-10">
            <span className="label text-primary">01 / ESTABLISHED</span>
            <div className="mt-4 font-mono text-4xl font-bold tracking-tight text-foreground md:text-5xl">2020</div>
            <p className="mt-2 text-sm text-muted-foreground">Fastest-growing construction chemical brand in Rajasthan.</p>
          </div>
          <div className="bg-background p-8 md:p-10">
            <span className="label text-primary">02 / QUALITY STANDARD</span>
            <div className="mt-4 font-mono text-4xl font-bold tracking-tight text-foreground md:text-5xl">ISO & ISI</div>
            <p className="mt-2 text-sm text-muted-foreground">Strict compliance, testing protocols & quality assurance.</p>
          </div>
          <div className="bg-background p-8 md:p-10">
            <span className="label text-primary">03 / PRODUCT PORTFOLIO</span>
            <div className="mt-4 font-mono text-4xl font-bold tracking-tight text-foreground md:text-5xl">12+ RANGE</div>
            <p className="mt-2 text-sm text-muted-foreground">Tile adhesives, epoxy grouts, AAC block mortars & admixtures.</p>
          </div>
          <div className="bg-background p-8 md:p-10">
            <span className="label text-primary">04 / ETHOS</span>
            <div className="mt-4 font-mono text-4xl font-bold tracking-tight text-foreground md:text-5xl">100%</div>
            <p className="mt-2 text-sm text-muted-foreground">Merit-driven, safety-first, zero corporate hierarchy delay.</p>
          </div>
        </div>
      </section>

      {/* Culture & Pillars Section */}
      <section className="border-t border-black/10 bg-secondary/30 py-28 md:py-36" data-testid="careers-pillars">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10">
          <Reveal>
            <span className="label text-primary">{t("careers.whyJoin")}</span>
            <h2 className="mt-6 max-w-3xl font-display text-4xl font-semibold leading-[0.95] tracking-tight md:text-6xl">
              {t("careers.whyTitle")}
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px bg-black/10 md:grid-cols-4">
            <Reveal delay={0.06} className="bg-background">
              <div className="group h-full p-8 transition-colors duration-300 hover:bg-secondary md:p-10">
                <FlaskConical className="h-9 w-9 text-primary" strokeWidth={1.3} />
                <h3 className="mt-8 font-display text-xl font-semibold">{t("careers.p1Title")}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("careers.p1Body")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.12} className="bg-background">
              <div className="group h-full p-8 transition-colors duration-300 hover:bg-secondary md:p-10">
                <ShieldCheck className="h-9 w-9 text-primary" strokeWidth={1.3} />
                <h3 className="mt-8 font-display text-xl font-semibold">{t("careers.p2Title")}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("careers.p2Body")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.18} className="bg-background">
              <div className="group h-full p-8 transition-colors duration-300 hover:bg-secondary md:p-10">
                <TrendingUp className="h-9 w-9 text-primary" strokeWidth={1.3} />
                <h3 className="mt-8 font-display text-xl font-semibold">{t("careers.p3Title")}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("careers.p3Body")}</p>
              </div>
            </Reveal>
            <Reveal delay={0.24} className="bg-background">
              <div className="group h-full p-8 transition-colors duration-300 hover:bg-secondary md:p-10">
                <Award className="h-9 w-9 text-primary" strokeWidth={1.3} />
                <h3 className="mt-8 font-display text-xl font-semibold">{t("careers.p4Title")}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{t("careers.p4Body")}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Open Positions Section */}
      <section id="open-positions" className="mx-auto max-w-[1600px] px-6 py-28 md:px-10 md:py-36" data-testid="careers-openings">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <span className="label text-primary">{t("careers.openingsEyebrow")}</span>
            <h2 className="mt-4 font-display text-4xl font-semibold leading-[0.95] tracking-tight md:text-6xl">
              {t("careers.openingsTitle")}
            </h2>
          </div>
          {/* Location Filter Tabs */}
          <div className="flex flex-wrap items-center gap-2 border-b border-black/10 pb-2">
            {[
              { id: "all", label: "All Locations (3 Vacancies)" },
              { id: "jaipur", label: "Jaipur (1)" },
              { id: "sikar", label: "Sikar (1)" },
              { id: "dausa", label: "Dausa (1)" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`careers-filter-${tab.id}`}
                className={`px-4 py-2 font-display text-sm font-medium transition-colors duration-150 ${
                  activeTab === tab.id
                    ? "bg-primary text-primary-foreground"
                    : "bg-secondary text-muted-foreground hover:bg-black/10 hover:text-foreground"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Urgent Hiring Callout Banner */}
        <div className="mt-12 border border-primary/30 bg-primary/[0.04] p-8 md:p-12 relative overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-primary/15 pb-6">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3.5 py-1 text-xs font-mono font-bold uppercase tracking-wider text-primary border border-primary/25">
              <span className="h-2 w-2 rounded-full bg-primary animate-ping" />
              <span>{t("careers.urgentBadge")}</span>
              <span className="text-muted-foreground">·</span>
              <span>3 Vacancies</span>
            </div>
            <span className="font-mono text-xs text-muted-foreground uppercase tracking-wider">
              Ashlar Buildchem Pvt. Ltd. · Rajasthan
            </span>
          </div>

          <div className="mt-6 grid gap-8 lg:grid-cols-[1.3fr_0.7fr]">
            <div>
              <h3 className="font-display text-2xl font-bold tracking-tight text-foreground sm:text-3xl md:text-4xl">
                {t("careers.urgentHiringTitle")}
              </h3>
              <p className="mt-3 text-base leading-relaxed text-muted-foreground">
                {t("careers.urgentHiringSubtitle")}
              </p>

              {/* Highlights Grid */}
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                <div className="border border-black/10 bg-background p-4">
                  <div className="flex items-center gap-2 text-xs font-mono font-semibold uppercase tracking-wider text-primary">
                    <IndianRupee className="h-4 w-4" />
                    <span>{t("careers.salaryLabel")}</span>
                  </div>
                  <div className="mt-1 font-display text-lg font-bold text-foreground">
                    {t("careers.salaryValue")}
                  </div>
                </div>

                <div className="border border-black/10 bg-background p-4">
                  <div className="flex items-center gap-2 text-xs font-mono font-semibold uppercase tracking-wider text-primary">
                    <Fuel className="h-4 w-4" />
                    <span>{t("careers.fuelLabel")}</span>
                  </div>
                  <div className="mt-1 text-xs leading-relaxed text-foreground font-medium">
                    {t("careers.fuelValue")}
                  </div>
                </div>
              </div>

              <div className="mt-4 border-l-2 border-primary bg-secondary/50 p-4 text-xs leading-relaxed text-muted-foreground">
                <strong className="text-foreground font-semibold">Candidate Profile: </strong>
                {t("careers.candidatePreference")}
              </div>
            </div>

            {/* Location Quick-Apply */}
            <div className="flex flex-col justify-between border-t border-black/10 pt-6 lg:border-l lg:border-t-0 lg:pl-8 lg:pt-0">
              <div>
                <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground block mb-3 font-semibold">
                  Open Vacancies by City:
                </span>
                <div className="space-y-3">
                  {[
                    { city: "Jaipur", vacancies: "1 Vacancy", role: "Field Sales Executive — Jaipur" },
                    { city: "Sikar", vacancies: "1 Vacancy", role: "Field Sales Executive — Sikar" },
                    { city: "Dausa", vacancies: "1 Vacancy", role: "Field Sales Executive — Dausa" },
                  ].map((loc) => (
                    <button
                      key={loc.city}
                      type="button"
                      onClick={() => selectRoleAndScroll(loc.role, loc.city)}
                      className="group flex w-full items-center justify-between border border-black/15 bg-background p-3.5 text-left transition-all hover:border-primary hover:bg-secondary/60 cursor-pointer"
                    >
                      <div className="flex items-center gap-2.5">
                        <MapPin className="h-4 w-4 text-primary" />
                        <span className="font-display text-base font-bold text-foreground group-hover:text-primary">
                          {loc.city}
                        </span>
                        <span className="rounded bg-primary/10 px-2 py-0.5 font-mono text-[0.7rem] font-semibold text-primary">
                          {loc.vacancies}
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-primary">
                        Apply <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:rotate-45" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-6 text-[0.75rem] text-muted-foreground font-mono">
                Official petrol expenses reimbursed. Home-to-work commuting expenses not covered.
              </div>
            </div>
          </div>
        </div>

        {/* Positions Grid */}
        <div className="mt-12 grid gap-px bg-black/10 md:grid-cols-3">
          {filteredPositions.map((pos, idx) => (
            <Reveal key={pos.id} delay={idx * 0.05} className="bg-background">
              <div className="group flex h-full flex-col justify-between p-8 transition-colors duration-200 hover:bg-secondary/40 md:p-10">
                <div>
                  <div className="flex items-center justify-between gap-4">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-0.5 font-mono text-[0.7rem] font-bold uppercase tracking-wider text-primary border border-primary/20">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                      {t("careers.urgentBadge")}
                    </span>
                    <span className="font-mono text-xs text-muted-foreground">
                      0{idx + 1}
                    </span>
                  </div>
                  <h3 className="mt-4 font-display text-2xl font-bold tracking-tight text-foreground transition-colors group-hover:text-primary">
                    {pos.title}
                  </h3>
                  <div className="mt-4 flex flex-wrap items-center gap-3 text-xs font-medium text-muted-foreground">
                    <span className="inline-flex items-center gap-1.5 font-semibold text-foreground">
                      <MapPin className="h-3.5 w-3.5 text-primary" /> {pos.location}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock className="h-3.5 w-3.5 text-primary" /> {pos.type}
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Briefcase className="h-3.5 w-3.5 text-primary" /> {pos.experience}
                    </span>
                  </div>

                  {/* Compensation & Fuel Perks */}
                  <div className="mt-4 space-y-2 border-y border-black/10 py-3.5 text-xs">
                    <div className="flex items-center gap-2 font-mono font-bold text-foreground">
                      <IndianRupee className="h-3.5 w-3.5 text-primary shrink-0" />
                      <span>{pos.salary}</span>
                    </div>
                    <div className="flex items-start gap-2 text-muted-foreground">
                      <Fuel className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                      <span className="leading-tight text-[0.75rem]">{pos.fuel}</span>
                    </div>
                  </div>

                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
                    {pos.summary}
                  </p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {pos.tags.map((tag) => (
                      <span
                        key={tag}
                        className="border border-black/10 bg-background px-2.5 py-1 font-mono text-[0.7rem] text-muted-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mt-8 border-t border-black/10 pt-6">
                  <button
                    onClick={() => selectRoleAndScroll(pos.title, pos.locationCity)}
                    data-testid={`careers-apply-role-${pos.id}`}
                    className="inline-flex w-full items-center justify-between bg-transparent py-2 font-display text-sm font-semibold tracking-wider text-foreground transition-colors group-hover:text-primary uppercase cursor-pointer"
                  >
                    <span>{t("careers.applyNow")}</span>
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Application Form Section */}
      <section ref={formRef} className="border-t border-black/10 bg-background" data-testid="careers-form-section">
        <div className="mx-auto grid max-w-[1600px] gap-px bg-black/10 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Left Form */}
          <div className="bg-background px-6 py-20 md:px-16 md:py-28">
            <span className="label text-primary">{t("careers.formEyebrow")}</span>
            <h2 className="mt-4 font-display text-4xl font-bold tracking-tight text-foreground md:text-5xl">
              {t("careers.formTitle")}
            </h2>
            <p className="mt-4 max-w-md text-base text-muted-foreground">
              {t("careers.formSubtitle")}
            </p>

            {submitted ? (
              <div className="mt-12 border border-primary/20 bg-secondary/50 p-8 text-foreground" data-testid="careers-success-box">
                <CheckCircle2 className="h-10 w-10 text-primary" />
                <h3 className="mt-4 font-display text-2xl font-semibold">Application Received</h3>
                <p className="mt-2 text-muted-foreground leading-relaxed">
                  {t("careers.successMsg")}
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 inline-flex items-center gap-2 border border-black/20 bg-background px-6 py-3 font-display text-sm font-medium hover:border-primary hover:text-primary cursor-pointer"
                >
                  Submit Another Application
                </button>
              </div>
            ) : (
              <form onSubmit={submitApplication} className="mt-12 space-y-8" data-testid="careers-form">
                <div className="grid gap-8 sm:grid-cols-2">
                  <LineInput
                    label={t("careers.fullName")}
                    required
                    placeholder={t("careers.namePlaceholder")}
                    value={form.name}
                    onChange={onNameChange}
                    error={errors.name}
                    data-testid="careers-input-name"
                  />
                  <LineInput
                    label={t("careers.email")}
                    required
                    type="email"
                    placeholder={t("careers.emailPlaceholder")}
                    value={form.email}
                    onChange={setField("email")}
                    error={errors.email}
                    data-testid="careers-input-email"
                  />
                  <LineInput
                    label={t("careers.phone")}
                    required
                    placeholder="+91"
                    value={form.phone}
                    onChange={onPhoneChange}
                    inputMode="numeric"
                    maxLength={10}
                    error={errors.phone}
                    data-testid="careers-input-phone"
                  />
                  <LineInput
                    label={t("careers.currentCompany")}
                    placeholder={t("careers.currentCompanyPlaceholder")}
                    value={form.company}
                    onChange={setField("company")}
                    data-testid="careers-input-company"
                  />
                </div>

                <div className="grid gap-8 sm:grid-cols-2">
                  <LineSelect
                    label={t("careers.roleApplied")}
                    required
                    value={form.position}
                    onChange={setField("position")}
                    data-testid="careers-select-position"
                  >
                    {schemaOptions.positions.map((pos) => (
                      <option key={pos} value={pos}>
                        {pos}
                      </option>
                    ))}
                  </LineSelect>

                  <LineSelect
                    label={t("careers.preferredLocation")}
                    required
                    value={form.location}
                    onChange={setField("location")}
                    data-testid="careers-select-location"
                  >
                    <option value="Jaipur">{t("careers.locJaipur")}</option>
                    <option value="Sikar">{t("careers.locSikar")}</option>
                    <option value="Dausa">{t("careers.locDausa")}</option>
                    <option value="Flexible">{t("careers.locFlexible")}</option>
                  </LineSelect>
                </div>

                <div className="grid gap-8 sm:grid-cols-2">
                  <LineSelect
                    label={t("careers.totalExperience")}
                    required
                    value={form.experience}
                    onChange={setField("experience")}
                    data-testid="careers-select-experience"
                  >
                    {schemaOptions.experience.map((exp) => (
                      <option key={exp} value={exp}>
                        {exp}
                      </option>
                    ))}
                  </LineSelect>

                  <LineSelect
                    label={t("careers.noticePeriod")}
                    value={form.noticePeriod}
                    onChange={setField("noticePeriod")}
                    data-testid="careers-select-notice"
                  >
                    {schemaOptions.noticePeriods.map((notice) => (
                      <option key={notice} value={notice}>
                        {notice}
                      </option>
                    ))}
                  </LineSelect>
                </div>

                {/* Resume Upload / Link */}
                <div className="space-y-3" data-testid="careers-resume-section">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <label className="label block text-muted-foreground">
                      {t("careers.uploadResumeLabel")}{" "}
                      <span className="text-xs font-normal text-muted-foreground/70">
                        ({t("careers.uploadResumeSubtext")})
                      </span>
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setResumeMode((prev) => (prev === "file" ? "url" : "file"));
                        setErrors((prev) => ({ ...prev, resume: "" }));
                      }}
                      className="text-xs font-medium text-primary hover:underline cursor-pointer"
                    >
                      {resumeMode === "file"
                        ? t("careers.orSwitchToUrl")
                        : t("careers.orSwitchToFile")}
                    </button>
                  </div>

                  {resumeMode === "file" ? (
                    <div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileSelect(file);
                        }}
                        data-testid="careers-file-input"
                      />

                      {resumeFile ? (
                        <div className="flex items-center justify-between border border-black/15 bg-secondary/30 p-4 transition-colors">
                          <div className="flex items-center gap-3 overflow-hidden">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center bg-primary/10 text-primary">
                              <FileText className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                              <p className="truncate font-display text-sm font-semibold text-foreground">
                                {resumeFile.name}
                              </p>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                <span>{formatFileSize(resumeFile.size)}</span>
                                <span>•</span>
                                <span className="text-primary font-medium">
                                  {t("careers.fileReadyToUpload")}
                                </span>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={removeSelectedFile}
                            className="p-2 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                            title={t("careers.removeFile")}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ) : (
                        <div
                          onDragOver={onDragOver}
                          onDragLeave={onDragLeave}
                          onDrop={onDrop}
                          onClick={() => fileInputRef.current?.click()}
                          className={`flex cursor-pointer flex-col items-center justify-center border-2 border-dashed p-6 text-center transition-all ${
                            isDragging
                              ? "border-primary bg-primary/5"
                              : "border-black/15 bg-background hover:border-black/30 hover:bg-secondary/20"
                          }`}
                          data-testid="careers-dropzone"
                        >
                          <UploadCloud className="h-8 w-8 text-muted-foreground" />
                          <p className="mt-2 font-display text-sm font-medium text-foreground">
                            {t("careers.dragOrBrowse")}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground">
                            PDF, DOCX up to 10MB
                          </p>
                        </div>
                      )}
                      {errors.resume && (
                        <p className="mt-2 text-sm text-primary">{errors.resume}</p>
                      )}
                    </div>
                  ) : (
                    <div>
                      <LineInput
                        label={t("careers.portfolioUrl")}
                        placeholder={t("careers.portfolioPlaceholder")}
                        value={form.resumeUrl}
                        onChange={setField("resumeUrl")}
                        error={errors.resumeUrl}
                        data-testid="careers-input-resume"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="label mb-3 block text-muted-foreground">{t("careers.statement")}</label>
                  <textarea
                    rows={4}
                    placeholder={t("careers.statementPlaceholder")}
                    value={form.message}
                    onChange={setField("message")}
                    data-testid="careers-textarea-statement"
                    className="w-full resize-none border-0 border-b border-black/20 bg-transparent px-0 py-3 font-display text-lg text-foreground outline-none transition-colors placeholder:text-black/25 focus:border-primary"
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="careers-submit-btn"
                  className="group inline-flex items-center gap-3 bg-primary px-10 py-5 text-primary-foreground transition-colors duration-200 hover:bg-foreground hover:text-background disabled:opacity-60 cursor-pointer"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  <span className="label">
                    {submitting
                      ? resumeFile
                        ? t("careers.uploadingAndSubmitting")
                        : t("careers.submitting")
                      : t("careers.submitApplication")}
                  </span>
                  {!submitting && (
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:rotate-45" />
                  )}
                </button>
              </form>
            )}
          </div>

          {/* Right Info Column */}
          <div className="relative flex flex-col justify-between overflow-hidden bg-secondary/30 px-6 py-20 md:px-16 md:py-28">
            <div className="relative space-y-12">
              <div>
                <span className="label text-primary">{t("careers.processTitle")}</span>
                <div className="mt-8 space-y-8">
                  <div className="border-l-2 border-primary pl-6">
                    <span className="font-mono text-sm font-bold text-foreground">{t("careers.step1")}</span>
                    <p className="mt-1 text-sm text-muted-foreground">{t("careers.step1Desc")}</p>
                  </div>
                  <div className="border-l-2 border-black/20 pl-6">
                    <span className="font-mono text-sm font-bold text-foreground">{t("careers.step2")}</span>
                    <p className="mt-1 text-sm text-muted-foreground">{t("careers.step2Desc")}</p>
                  </div>
                  <div className="border-l-2 border-black/20 pl-6">
                    <span className="font-mono text-sm font-bold text-foreground">{t("careers.step3")}</span>
                    <p className="mt-1 text-sm text-muted-foreground">{t("careers.step3Desc")}</p>
                  </div>
                  <div className="border-l-2 border-black/20 pl-6">
                    <span className="font-mono text-sm font-bold text-foreground">{t("careers.step4")}</span>
                    <p className="mt-1 text-sm text-muted-foreground">{t("careers.step4Desc")}</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-black/10 pt-10">
                <span className="label text-muted-foreground">{t("careers.hrHelp")}</span>
                <div className="mt-4 space-y-4">
                  <a
                    href="mailto:hr@ashlarbuildchem.com"
                    className="flex items-center gap-3 font-display text-lg font-semibold text-foreground hover:text-primary transition-colors"
                  >
                    <Mail className="h-5 w-5 text-primary" />
                    <span>hr@ashlarbuildchem.com</span>
                  </a>
                  <a
                    href="tel:18003090808"
                    className="flex items-center gap-3 font-display text-lg font-semibold text-foreground hover:text-primary transition-colors"
                  >
                    <Phone className="h-5 w-5 text-primary" />
                    <span>1800 309 0808 (Toll Free)</span>
                  </a>
                </div>
              </div>

              <div className="border-t border-black/10 pt-10">
                <span className="label text-muted-foreground">{t("careers.officeTitle")}</span>
                <div className="mt-3 flex items-start gap-3">
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-primary" />
                  <span className="font-display text-base leading-relaxed text-foreground">
                    247(NE), Scheme 4C, Jamunapuri,
                    <br />
                    Jaipur — 302039, Rajasthan, India
                  </span>
                </div>
              </div>
            </div>

            <div className="relative mt-16 border-t border-black/10 pt-8">
              <span className="font-mono text-xs text-muted-foreground">
                Ashlar Buildchem is an Equal Opportunity Employer. We celebrate technical diversity and build without compromise.
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
